import { useCallback } from 'react';

import { _getTextWidth } from '@dynatrace/strato-components/typography';
import Typography from '@dynatrace/strato-design-tokens/typography';

import { applyEllipsis } from './utils/canvas-label-utils.js';
import { useCanvas } from '../../../core/components/canvas/hooks/useCanvas.js';
import { getFontDetails } from '../../../core/utils/get-font-details.js';
import {
  DEFAULT_CLUSTER_LABEL_FONTSIZE,
  DEFAULT_CLUSTER_LABEL_LINE_HEIGHT,
  DEFAULT_FONT_WEIGHT,
  TEXT_ALIGN,
} from '../../constants.js';
import { useTruncationMode } from '../../hooks/useTruncationMode.js';

export interface TreeMapNodeLabelProps {
  x: number;
  y: number;
  color: string;
  label: string;
  maxWidth: number;
  opacity?: number;
}

export const ItemFontStyles = {
  fontWeight: Typography.Text.Small.Default.Weight,
  fontSize: Typography.Text.Small.Emphasized.Size,
  fontFamily: Typography.Text.Small.Default.Family,
};

export const TreeMapNodeLabel = (props: TreeMapNodeLabelProps) => {
  const { x, y, color, label, maxWidth, opacity = 1 } = props;

  const truncationMode = useTruncationMode();

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.font = getFontDetails(
        DEFAULT_FONT_WEIGHT,
        DEFAULT_CLUSTER_LABEL_FONTSIZE,
        DEFAULT_CLUSTER_LABEL_LINE_HEIGHT,
      );
      ctx.fillStyle = color;
      const estimatedLabelWidth = _getTextWidth(label, ItemFontStyles);

      const sanitizedLabel =
        estimatedLabelWidth > maxWidth
          ? applyEllipsis(ctx, label, maxWidth, truncationMode)
          : label;

      ctx.textAlign = TEXT_ALIGN.Center;
      ctx.globalAlpha = opacity;
      ctx.fillText(sanitizedLabel, x, y, maxWidth);

      return false;
    },
    [color, label, maxWidth, truncationMode, opacity, x, y],
  );

  useCanvas('treemap-label', draw);

  return null;
};
